import React, { useState, useRef, useEffect } from "react";
import { MentionsInput, Mention } from "react-mentions";
import { Paperclip, Send, Smile } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import "./Chat.css";

const MessageInput = ({ onSend, users, onTyping, replyTo, onCancelReply }) => {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // Create object URLs once per file list change and revoke old ones on cleanup
  useEffect(() => {
    const urls = attachments.map(f => f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
    setPreviewUrls(urls);
    return () => { urls.forEach(u => { if (u) URL.revokeObjectURL(u); }); };
  }, [attachments]);

  const handleSend = () => {
    if (text.trim() || attachments.length > 0) {
      onSend({
        text,
        attachments,
        parent_id: replyTo ? replyTo._id : null
      });
      setText("");
      setAttachments([]);
      setShowEmojiPicker(false);
    }
  };

  const onEmojiClick = (emojiData) => {
    setText(prev => prev + emojiData.emoji);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments([...attachments, ...files]);
    e.target.value = "";
  };

  const handleChange = (e) => {
    setText(e.target.value);
    if (onTyping) onTyping(e.target.value.length > 0);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          const ext = item.type.split('/')[1]?.replace('jpeg', 'jpg') || 'png';
          const namedFile = new File([file], `pasted-image-${Date.now()}.${ext}`, { type: item.type });
          setAttachments(prev => [...prev, namedFile]);
        }
        break;
      }
    }
  };

  const mentionStyle = {
    backgroundColor: "#e8f0fe",
    borderRadius: "3px",
  };

  return (
    <div className="message-input-container">
      {replyTo && (
        <div style={{ background: '#f1f3f4', padding: '10px 15px', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', gap: '10px', border: '1px solid #e0e0e0', borderBottom: 'none' }}>
          <div style={{ borderLeft: '3px solid #1a73e8', paddingLeft: '10px', flex: 1, overflow: 'hidden' }}>
            <div style={{ color: '#1a73e8', fontSize: '11px', fontWeight: '700' }}>Replying to {replyTo.sender_name}</div>
            <div style={{ fontSize: '13px', color: '#5f6368', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{replyTo.text || (replyTo.attachments?.length > 0 ? "Attachment" : "...")}</div>
          </div>
          <button onClick={onCancelReply} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#70757a', fontWeight: 'bold', fontSize: '18px' }}>
            ×
          </button>
        </div>
      )}
      {attachments.length > 0 && (
        <div className="attachment-previews" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px', padding: '10px', background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: replyTo ? '0' : '12px 12px 0 0' }}>
          {attachments.map((file, i) => {
            const previewUrl = previewUrls[i];
            return (
              <div key={i} className="attachment-tag" style={{ background: 'white', border: '1px solid #e0e0e0', padding: '5px', borderRadius: '8px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                {previewUrl ? (
                  <img src={previewUrl} alt="preview" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }} />
                ) : (
                  <Paperclip size={16} color="#5f6368" />
                )}
                <span style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '500' }}>{file.name}</span>
                <span
                  onClick={() => setAttachments(attachments.filter((_, idx) => idx !== i))}
                  style={{ marginLeft: '5px', cursor: 'pointer', color: '#ff4d4f', fontWeight: 'bold' }}
                >
                  ×
                </span>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#f8f9fa", padding: "8px 15px", borderRadius: "12px", border: "1px solid #e0e0e0" }}>
        <button onClick={() => fileInputRef.current.click()} style={{ background: "none", border: "none", cursor: "pointer", padding: "5px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Paperclip size={20} color="#5f6368" />
        </button>
        <input
          type="file"
          multiple
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <div style={{ flex: 1, maxHeight: '100px', overflowY: 'auto' }}>
          <MentionsInput
            value={text}
            onChange={handleChange}
            placeholder="Type your message here..."
            className="mentions-input"
            style={{
              control: {
                fontSize: 14,
                fontWeight: 'normal',
              },
              '&multiLine': {
                control: {
                  fontFamily: 'inherit',
                  minHeight: 20,
                },
                highlighter: {
                  padding: 9,
                  border: 'none',
                  color: 'transparent',
                },
                input: {
                  padding: 9,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                },
              },
            }}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          >
            <Mention
              trigger="@"
              data={users}
              style={{
                backgroundColor: '#0DDFAB',
                color: 'transparent',
                borderRadius: '2px',
              }}
              displayTransform={(id, display) => `@${display}`}
              appendSpaceOnAdd
            />
          </MentionsInput>
        </div>

        <div style={{ position: "relative" }}>
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "5px", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <Smile size={20} color={showEmojiPicker ? "#1a73e8" : "#5f6368"} />
          </button>
          {showEmojiPicker && (
            <div style={{ position: "absolute", bottom: "45px", right: "0", zIndex: 1000 }} ref={emojiPickerRef}>
              <EmojiPicker onEmojiClick={onEmojiClick} />
            </div>
          )}
        </div>

        <button
          onClick={handleSend}
          disabled={!text.trim() && attachments.length === 0}
          style={{
            background: "#1a73e8",
            color: "#fff",
            border: "none",
            borderRadius: "50%",
            width: "36px",
            height: "36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            opacity: (text.trim() || attachments.length > 0) ? 1 : 0.5
          }}
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};

export default MessageInput;
